<template>
  <div class="popUpWrap">
    <section class="popup light">
      <div class="topArea">
        <h4>{{ t("profile-work-experience") }}</h4>
        <div class="exit" @click.stop="onClickClose"></div>
      </div>
      <div class="scrollY">
        <ul class="infoList flex">
          <li class="flex">
            <div class="tit">{{ t("profile-work-experience-company-name") }}</div>
            <input
              id="workExperienceCompanyNameInput"
              v-model="workExperienceData.companyName"
              type="text"
              :class="{error : !isValidStringCompanyName}"
              :placeholder="t('profile-work-experience-company-name-placeholder')">
            <p v-if="!isValidStringCompanyName" class="errorMessage">
              <span></span>
              {{ t("profile-work-experience-company-name-error") }}
            </p>
          </li>
          <li class="flex">
            <div class="tit">{{ t("profile-work-experience-job-title") }}</div>
            <input
              id="workExperienceJobTitleInput"
              v-model="workExperienceData.jobTitle"
              type="text"
              :class="{error : !isValidStringJobTitle}"
              :placeholder="t('profile-work-experience-job-title-placeholder')">
            <p v-if="!isValidStringJobTitle" class="errorMessage">
              <span></span>
              {{ t("profile-work-experience-job-title-error") }}
            </p>
          </li>
          <li class="flex">
            <div class="tit">{{ t("profile-work-experience-location") }} <span>{{ t("common-optional", 0) }}</span>
            </div>
            <input
              id="workExperienceLocationInput"
              v-model="workExperienceData.location"
              type="text"
              :class="{error : !isValidStringLocation}"
              :placeholder="t('profile-work-experience-location-placeholder')">
            <p v-if="!isValidStringLocation" class="errorMessage">
              <span></span>
              {{ t("profile-work-experience-location-error") }}
            </p>
          </li>
          <li class="flex">
            <ul class="checkBoxList">
              <li>
                <input id="item1" v-model="workExperienceData.currentlyEmployedFlag" type="checkbox">
                <label for="item1">{{ t("profile-work-experience-period") }}</label>
              </li>
            </ul>
            <ul v-if="isLoading" class="dateArea flex">
              <InformationPeriodSelect
                :title="t('common-start-date')"
                :default-button-string-month="t('common-month')"
                :default-button-string-year="t('common-year')"
                :default-date="workExperienceData.startDate"
                :is-month-open-flag="periodMenuFlag.startMonth"
                :is-year-open-flag="periodMenuFlag.startYear"
                @onChangeDate="onChangeStartData"
                @onChangeMenuFlag="onChangeStartMenuFlag"
              />
              <InformationPeriodSelect
                v-if="!workExperienceData.currentlyEmployedFlag"
                :title="t('common-end-date')"
                :default-button-string-month="t('common-month')"
                :default-button-string-year="t('common-year')"
                :default-date="workExperienceData.endDate"
                :is-month-open-flag="periodMenuFlag.endMonth"
                :is-year-open-flag="periodMenuFlag.endYear"
                :is-error="!isValidPeriod"
                @onChangeDate="onChangeEndData"
                @onChangeMenuFlag="onChangeEndMenuFlag"
              />
              <p v-if="!isValidPeriod" class="errorMessage"><span></span>{{ t("profile-period-error") }}</p>
              <li class="bar"></li>
            </ul>
          </li>
          <li class="flex">
            <div class="tit">
              {{ t("common-description") }}
              <span>{{ t("common-optional", 0) }}</span>
            </div>
            <textarea
              id="workExperienceDescriptionInput"
              v-model="workExperienceData.description"
              :class="{error : !isValidStringDescription}"
              :placeholder="t('profile-work-experience-description-placeholder')"
              style="height: 134px;" />
            <p v-if="!isValidStringDescription" class="errorMessage">
              <span></span>{{ t("profile-work-experience-description-error") }}
            </p>
          </li>
        </ul>
        <div class="btnArea">
          <button
            v-if="workExperienceData.id !== 0"
            id="workExperienceDeleteBtn"
            class="btn cancel"
            @click.stop="onClickOpenDeletePopup"
          >
            <span></span>
            {{ t("common-delete") }}
          </button>
          <button
            class="btn"
            :class="{ disabled: !isValidDisableButton }"
            :disabled="!isValidDisableButton"
            @click.stop="onClickSave"
          >
            {{ t("common-save") }}
          </button>
        </div>
      </div>
    </section>
    <div v-if="isOpenDeletePopup" class="popUpWrap">
      <section class="popup dark basic">
        <div class="topArea">
          <div id="workExperienceRemovePopupExitBtn" class="exit" @click.stop="isOpenDeletePopup = false"></div>
        </div>
        <div class="scrollY">
          <h5>{{ t('profile-work-experience-entry-delete-title') }}</h5>
          <div class="btnArea">
            <button class="cancel" @click.stop="isOpenDeletePopup = false">{{ t("common-cancel") }}</button>
            <button @click.stop="onClickDelete">{{ t("common-delete") }}</button>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { useDialogStore } from "~/store/dialog/dialogStore";
import { onMounted } from "#imports";
import { useToastStore } from "~/store/toast/toastStore";
import { useProfileService } from "~~/application/composables/service/useProfileService";
import {
  ProfileUserInformationWorkExperienceModel, ProfileWorkExperienceCreateModel
} from "~~/package/mapper/profile/model/ProfileUserInformationWorkExperienceModel";
import InformationPeriodSelect
  from "~~/application/components/Common/Dialog/dialog/Profile/InformationPeriodSelect.vue";
import { ToastType } from "~/components/Common/Toast/model/ToastType";

const { t } = useI18n();
const { $i18n } = useNuxtApp();
const lang = computed(() =>
  $i18n.locale.value.toUpperCase()
);

const dialogStore = useDialogStore();
const toastStore = useToastStore();
const profileService = useProfileService();
const workExperienceData = ref(new ProfileUserInformationWorkExperienceModel());
const isLoading = ref(false);
const isOpenDeletePopup = ref(false);

const periodMenuFlag = ref({
  startMonth: false,
  startYear: false,
  endMonth: false,
  endYear: false
})

onMounted(() => {
  workExperienceData.value = { ...dialogStore.options.props.workExperience };
  isLoading.value = true;
});

const isValidStringCompanyName = computed(() => {
  return workExperienceData.value.companyName.length <= 50;
});

const isValidStringJobTitle = computed(() => {
  return workExperienceData.value.jobTitle.length <= 50;
});

const isValidStringLocation = computed(() => {
  return workExperienceData.value.location.length <= 150;
});

const isValidStringDescription = computed(() => {
  return workExperienceData.value.description.length <= 1500;
});

const isValidPeriod = computed(() => {
  if(!workExperienceData.value.startDate || !workExperienceData.value.endDate || workExperienceData.value.currentlyEmployedFlag)
    return true;
  else {
    return new Date(workExperienceData.value.endDate).getTime() >= new Date(workExperienceData.value.startDate).getTime()
  }
})

const isValidDisableButton = computed(() => {
  return workExperienceData.value.companyName.length > 0 &&
    workExperienceData.value.jobTitle.length > 0 &&
    workExperienceData.value.startDate?.length > 0 &&
    (workExperienceData.value.currentlyEmployedFlag || workExperienceData.value.endDate?.length > 0) &&
    isValidStringCompanyName.value &&
    isValidStringJobTitle.value &&
    isValidStringLocation.value &&
    isValidStringDescription.value &&
    isValidPeriod.value
});

const onChangeStartData = (data: string) => {
  workExperienceData.value.startDate = data;
}

const onChangeEndData = (data: string) => {
  workExperienceData.value.endDate = data;
}

const onClickSave = () => {
  const editWorkExperience: ProfileWorkExperienceCreateModel = {
    id: workExperienceData.value.id,
    companyName: workExperienceData.value.companyName,
    jobTitle: workExperienceData.value.jobTitle,
    location: workExperienceData.value.location,
    currentlyEmployedFlag: workExperienceData.value.currentlyEmployedFlag,
    startDate: workExperienceData.value.startDate,
    endDate: workExperienceData.value.endDate,
    description: workExperienceData.value.description
  }

  if (workExperienceData.value.id === 0) {
    profileService.addWorkExperience(editWorkExperience).then(response => {
      if(response){
        toastStore.openToast({
          toastType: ToastType.SUCCESS,
          contents: t(`profile-change-success`)
        })
        dialogStore.options.confirm?.event();
        onClickClose();
      }
      else {
        toastStore.openToast({
          toastType: ToastType.FAILED,
          contents: t(`profile-change-fail`)
        })
      }
    }).catch(() => {
      toastStore.openToast({
        toastType: ToastType.FAILED,
        contents: t(`profile-change-fail`)
      })
    })
  }
  else {
    profileService.modifyWorkExperience(editWorkExperience).then(response => {
      if(response){
        toastStore.openToast({
          toastType: ToastType.SUCCESS,
          contents: t(`profile-change-success`)
        })
        dialogStore.options.confirm?.event();
        onClickClose();
      }
      else {
        toastStore.openToast({
          toastType: ToastType.FAILED,
          contents: t(`toast-message.fail-change`)
        })
      }
    }).catch(() => {
      toastStore.openToast({
        toastType: ToastType.FAILED,
        contents: t(`toast-message.fail-change`)
      })
    })
  }
};

const onChangeStartMenuFlag = (menu: string, flag: boolean) => {
  if(menu === "month"){
    periodMenuFlag.value.startMonth = flag
    periodMenuFlag.value.startYear = false;
    periodMenuFlag.value.endMonth = false;
    periodMenuFlag.value.endYear = false;
  }else if(menu === "year"){
    periodMenuFlag.value.startMonth = false
    periodMenuFlag.value.startYear = flag;
    periodMenuFlag.value.endMonth = false;
    periodMenuFlag.value.endYear = false;
  }
}

const onChangeEndMenuFlag = (menu: string, flag: boolean) => {
  if(menu === "month"){
    periodMenuFlag.value.startMonth = false;
    periodMenuFlag.value.startYear = false;
    periodMenuFlag.value.endMonth = flag;
    periodMenuFlag.value.endYear = false;
  }else if(menu === "year"){
    periodMenuFlag.value.startMonth = false
    periodMenuFlag.value.startYear = false;
    periodMenuFlag.value.endMonth = false;
    periodMenuFlag.value.endYear = flag;
  }
}

const onClickOpenDeletePopup = () => {
  isOpenDeletePopup.value = true;
};

const onClickDelete = () => {
  isOpenDeletePopup.value = false;
  profileService.deleteWorkExperience(workExperienceData.value.id).then(response => {
    if (response) {
      toastStore.openToast({
        toastType: ToastType.SUCCESS,
        contents: t(`profile-work-experience-entry-delete-success`)
      });
      dialogStore.options.confirm?.event();
      onClickClose();
    } else {
      toastStore.openToast({
        toastType: ToastType.FAILED,
        contents: t(`profile-work-experience-entry-delete-fail`)
      });
    }
  }).catch(() => {
    toastStore.openToast({
      toastType: ToastType.FAILED,
      contents: t(`profile-work-experience-entry-delete-fail`)
    });
  });
}

const onClickClose = () => {
  dialogStore.close();
};
</script>

